<template>
  <div
    :title="label"
    :class="[
      'animate__animated',
      $style.component,
      placedToRef && $style.placed,
      placedToRef && !canMovePlaced && $style.immovable,
      placedToRef && !keepVisible && 'animate__bounceOut',
      placedToRef && keepVisible && 'animate__pulse',
      hidden && $style.hidden,
      isAnimatingWrong && 'animate__wobble',
      isReturning && $style.returning
    ]"
    ref="componentRef"
    :style="{
      zIndex: isDragging ? 99 : zIndex
    }"
  >
    <movable
      target="componentRef"
      @start="onStart"
      @move="onMove"
      @complete="onStop"
    >
      <ItemGraphics
      :imageUrl="imageUrl"
      :label="label"
      :size="size"
      />
      <img src="../../assets/images/draggable-icon.svg" :class="[$style.draggableIcon]" alt="Draggable icon" />
    </movable>
  </div>
</template>

<script>
import {ref, watch} from '@vue/composition-api'

import useTimeouts from '@/composition/useTimeouts'

export default {
  props: {
    label: {type: String, required: false},
    imageUrl: {type: String, required: false},
    initialPosition: {type: Array, required: false},
    size: {type: Array, required: false},
    zIndex: {type: Number, required: false},
    placedToRef: {type: Object, required: false},
    boardRef: {required: true},
    keepVisible: {type: Boolean, required: false},
    canMovePlaced: {type: Boolean, required: false, default: false},
  },
  setup (props, context) {
    const hasMoved = ref(false)
    const componentRef = ref()
    const setTimeout = useTimeouts()
    const hidden = ref(false)
    const isAnimatingWrong = ref(false)
    const isDragging = ref(false)
    const isReturning = ref(false)

    watch(() => [props.initialPosition, componentRef.value], () => {
      if (componentRef.value && props.initialPosition && !hasMoved.value) {
        setPosition(props.initialPosition[0], props.initialPosition[1])
      }
    }, {
      immediate: true,
    })

    watch(() => [props.placedToRef, props.keepVisible], ([placedToRef]) => {
      if (!placedToRef) return
      if (props.keepVisible) {
        setToPlace()
      } else {
        setTimeout(() => {
          hidden.value = true
        }, 1000)
      }
    })

    if (props.initialPosition) {
      setPosition(props.initialPosition[0], props.initialPosition[1])
    }

    return {
      componentRef,
      refreshPosition,
      onStart,
      onMove,
      onStop,
      hidden,
      isAnimatingWrong,
      isDragging,
      isReturning,
      returnToPlace () {
        isReturning.value = true
        if (props.initialPosition) {
          setTimeout(() => {
            setPosition(props.initialPosition[0], props.initialPosition[1])
          })
        } else if (props.placedToRef) {
          setToPlace()
        }
        setTimeout(() => {
          isReturning.value = false
        }, 250)
      },
      setPosition,
      goToPlace (x, y) {
        isReturning.value = true
        setTimeout(() => {
          setPosition(x, y)
        })
        setTimeout(() => {
          isReturning.value = false
        }, 250)
      },
      animateWrong () {
        isAnimatingWrong.value = true
        setTimeout(() => {
          isAnimatingWrong.value = false
        }, 1000)
      },
    }

    function onStart () {
      hasMoved.value = true
      isDragging.value = true
      context.emit('start')
    }

    function onMove () {
      keepInBounds()
      context.emit('move')
    }

    function onStop () {
      isDragging.value = false
      context.emit('stop')
    }

    function refreshPosition () {
      if (!props.placedToRef) {
        keepInBounds()
      } else {
        setToPlace()
      }
    }

    function setToPlace () {
      if (!props.placedToRef) return
      const {
        offsetLeft: dropToItemLeft,
        offsetTop: dropToItemTop,
      } = props.placedToRef.$el
      setPosition(dropToItemLeft, dropToItemTop)
    }

    function setPosition (x, y) {
      if (!componentRef.value) {
        return
      }

      componentRef.value.style.left = `${x || 0}px`
      componentRef.value.style.top = `${y || 0}px`
    }

    function keepInBounds () {
      const {
        x: itemX,
        y: itemY,
      } = getItemPosition()
      const {
        width: boardWidth,
        height: boardHeight,
      } = getBoardDimensions()
      const {
        width: itemWidth,
        height: itemHeight,
      } = getItemDimensions()

      let newX = itemX
      let newY = itemY

      const minX = itemWidth / 2
      const minY = itemHeight / 2
      const maxX = boardWidth - itemWidth / 2
      const maxY = boardHeight - itemHeight / 2

      if (itemX < minX) newX = minX
      if (itemY < minY) newY = minY
      if (itemX > maxX) newX = maxX
      if (itemY > maxY) newY = maxY

      if (newX !== itemX || newY !== itemY) {
        setPosition(newX, newY)
      }
    }

    function getItemDimensions () {
      const {
        width,
        height,
      } = componentRef.value.firstChild.getBoundingClientRect()
      return {width, height}
    }

    function getItemPosition () {
      const {
        offsetLeft: x,
        offsetTop: y,
      } = componentRef.value
      return {x, y}
    }

    function getBoardDimensions () {
      const {
        width,
        height,
      } = props.boardRef.getBoundingClientRect()
      return {width, height}
    }
  },
}
</script>

<style module lang="scss">
.component {
  position: absolute;
  user-select: none;

  .draggableIcon {
    position: absolute;
    right: -20%;
    right: max(-20%, -40px);
    top: 50%;
    transform: translateY(-50%);
    border-radius: 50%;
    padding: 5px;
    width: 20px;
    height: 20px;
    background-color: #ced4d6;
    box-shadow: -4px -4px 4px #ffffff2b, 4px 4px 4px #19191934;
  }

  &.placed {
    &.immovable {
      pointer-events: none;
    }

    &.hidden {
      display: none;
    }
  }

  &.returning {
    transition: top 0.25s ease-in-out, left 0.25s ease-in-out;
  }

  > * {
    transform: translate(-50%, -50%);
  }
}
</style>
